import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Search } from 'lucide-react';
import { BACKEND_URL } from '../lib/config';

const ACTION_COLORS = {
  LOGIN: 'text-[#2BB5D8] border-[#2BB5D8]/30',
  UPLOAD_EVALUATION: 'text-[#F7941D] border-[#F7941D]/30',
  GENERATE_PDF: 'text-green-400 border-green-400/30',
  AI_REMARK: 'text-purple-400 border-purple-400/30',
  UPLOAD_TEMPLATE: 'text-yellow-400 border-yellow-400/30',
  CREATE_COACH: 'text-blue-400 border-blue-400/30',
  UPDATE_COACH: 'text-orange-400 border-orange-400/30',
  DELETE_COACH: 'text-red-400 border-red-400/30',
  PASSWORD_RESET: 'text-orange-400 border-orange-400/30',
  SAVE_REMARKS: 'text-teal-400 border-teal-400/30',
};

const ACTION_LIST = [
  'LOGIN', 'UPLOAD_EVALUATION', 'GENERATE_PDF', 'AI_REMARK',
  'UPLOAD_TEMPLATE', 'CREATE_COACH', 'UPDATE_COACH', 'DELETE_COACH',
  'PASSWORD_RESET', 'SAVE_REMARKS',
];

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/audit-logs`, {
        params: { page, page_size: PAGE_SIZE, action: actionFilter || undefined },
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [page, actionFilter]); 

  const filtered = logs.filter(l =>
    l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.details?.toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield size={20} className="text-primary" /> Audit Logs
          </h1>
          <p className="text-sm text-muted-foreground">{total} total events</p>
        </div>

        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search user or action details..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-[#111827] border-border/60 h-9"
            />
          </div>
          <Select value={actionFilter || "all"} onValueChange={v => setActionFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 bg-[#111827] border-border/60 h-9"><SelectValue placeholder="All Actions" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-border/60">
              <SelectItem value="all">All Actions</SelectItem>
              {ACTION_LIST.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-[#111827] border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-2">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-secondary/50 rounded animate-pulse" />)}</div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-10">No audit logs found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4">Action</th>
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4">User</th>
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4 hidden sm:table-cell">Details</th>
                      <th className="text-left text-xs text-muted-foreground font-medium py-3 px-4 hidden md:table-cell">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {filtered.map(log => (
                      <tr key={log.id || log.timestamp} className="hover:bg-secondary/30">
                        <td className="py-2.5 px-4">
                          <Badge variant="outline" className={`text-[10px] ${ACTION_COLORS[log.action] || 'text-muted-foreground border-border/60'}`}>
                            {log.action}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-4 text-sm">{log.user_name}</td>
                        <td className="py-2.5 px-4 text-xs text-muted-foreground hidden sm:table-cell max-w-xs truncate">{log.details}</td>
                        <td className="py-2.5 px-4 text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">{formatTime(log.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2">
            <button className="text-xs text-muted-foreground hover:text-foreground" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span className="text-xs text-muted-foreground">Page {page}</span>
            <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
